/**
 * 业务环境跟构建模式走，不要在源码里写死 production/test。
 * Jenkins 已按分支选脚本：
 * - master → npm run build → .env.production → production
 * - test   → npm run test  → .env.test       → test
 *
 * VUE_APP_RUNTIME_ENV：production | test | auto
 * - production / test：强制该环境（忽略域名）
 * - auto：huawei-test* → 测试，其余（含本地）→ 生产
 *
 * 本地开发：.env.development 默认 production，与 vue `/api` 代理到 appv2 一致。
 * 本地要切测试时改 VUE_APP_RUNTIME_ENV=test，并同步 vue `/api` 代理到 huawei-test-appv2。
 */
function readBuildRuntimeEnv() {
  const raw = String(process.env.VUE_APP_RUNTIME_ENV || "auto").trim().toLowerCase();
  if (raw === "production" || raw === "test" || raw === "auto") return raw;
  return "auto";
}

export const RUNTIME_ENV = readBuildRuntimeEnv();

const ENV_HOSTS = {
  production: {
    api: "//appv2.sscanmou.com/app",
    report: "//admin.sscanmou.com/report-xx",
    webclient: "https://webclient.sscanmou.com",
    admin: "https://admin.sscanmou.com",
  },
  test: {
    api: "https://huawei-test-appv2.sscanmou.com/app",
    // 旧地址会 302 到 client-report-test，导致 iframe 的真实 origin
    // 与父页按 src 推导出的 origin 不一致，报告解读消息会被安全校验丢弃。
    report: "https://client-report-test.sscanmou.com/report-xx",
    webclient: "https://huawei-test-webclient.sscanmou.com",
    admin: "https://huawei-test-admin.sscanmou.com",
  },
};

const LOCAL_PROJECT_PORTS = {
  clientReport: 9002,
};

const getRuntimeHostname = () =>
  (typeof window !== "undefined" && window.location && window.location.hostname) || "";

export const isLocalHostname = hostname => {
  const host = hostname || getRuntimeHostname();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(host) ||
    /^10\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host)
  );
};

export const isTestHostname = hostname => /huawei-test/i.test(hostname || getRuntimeHostname());

/** 解析后的业务环境：production | test */
export function resolveRuntimeEnv() {
  if (RUNTIME_ENV === "production" || RUNTIME_ENV === "test") {
    return RUNTIME_ENV;
  }
  // auto：测试域名走测试；本地与正式域名默认生产（本地接口靠 vue 代理）
  if (isTestHostname()) {
    return "test";
  }
  return "production";
}

export function getEnvHosts(env = resolveRuntimeEnv()) {
  return ENV_HOSTS[env] || ENV_HOSTS.production;
}

/**
 * 本地开发始终把三端页面互跳改到本机端口；
 * 接口域名仍由 resolveRuntimeEnv / 本地代理决定（可本地对接生产）。
 */
export function shouldUseLocalProjectPages() {
  return isLocalHostname() || process.env.NODE_ENV === "development";
}

export function getApiHost() {
  // 本地走 vue 代理，避免直连生产 CORS；代理目标在 vue.config.js 配置
  if (shouldUseLocalProjectPages()) {
    return "/api";
  }
  return getEnvHosts().api;
}

export function getReportHost() {
  // 本地嵌套本机 client-report（9002），内容接口仍由 report 项目代理到生产/测试
  if (shouldUseLocalProjectPages()) {
    const hostname = getRuntimeHostname() || "localhost";
    const protocol =
      typeof window !== "undefined" && window.location ? window.location.protocol : "http:";
    return `${protocol}//${hostname}:${LOCAL_PROJECT_PORTS.clientReport}`;
  }
  return getEnvHosts().report;
}
